import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Clock3, Users } from 'lucide-react'
import { SectionHeading } from '@/components/generals/section-heading'
import { reasons } from '@/constants/content'
import { hours } from '@/constants/site'

export const WhyChooseUs: FC = (): ReactNode => {
    return (
        <section
            id='why-us'
            aria-labelledby='why-us-title'
            className='relative overflow-hidden bg-background py-20 sm:py-24 lg:py-28'
        >
            <div className='mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[0.92fr_1.08fr] lg:gap-20'>
                <div className='relative mx-auto w-full max-w-md lg:max-w-none'>
                    <div aria-hidden='true' className='absolute -bottom-4 -right-4 size-full rounded-[2rem] border border-gold/40' />

                    <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] shadow-2xl shadow-plum/15'>
                        <Image
                            src='/images/salon-interior.jpg'
                            alt='Bright white and gold beauty parlour with styling chairs and mirrors'
                            fill
                            sizes='(min-width: 1024px) 40vw, (min-width: 640px) 28rem, 90vw'
                            className='object-cover object-[50%_62%]'
                        />
                    </div>

                    <div className='absolute -left-3 bottom-8 max-w-[15.5rem] rounded-2xl border border-gold/30 bg-white p-4 shadow-xl shadow-plum/15 sm:-left-8'>
                        <p className='flex items-center gap-2 font-heading text-sm font-semibold text-plum'>
                            <Users className='size-4 text-gold-dark' aria-hidden='true' />
                            Women-only parlour
                        </p>

                        <p className='mt-1.5 text-xs leading-relaxed text-muted-foreground'>
                            A calm, private and comfortable space, just for you.
                        </p>
                    </div>

                    <div className='absolute -right-3 top-8 rounded-2xl bg-plum-deep px-4 py-3 text-ivory shadow-xl shadow-plum/25 sm:-right-6'>
                        <p className='flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-gold-light'>
                            <Clock3 className='size-3.5' aria-hidden='true' />
                            {hours.days}
                        </p>

                        <p className='mt-1 font-heading text-sm font-semibold'>
                            {hours.label}
                        </p>
                    </div>
                </div>

                <div>
                    <SectionHeading
                        id='why-us-title'
                        align='left'
                        eyebrow='Why choose us'
                        title='Beauty with care, in every detail'
                        description='At Anushka Beauty Parlour every visit is about you: your skin, your style and your comfort. Here is what makes women across North Delhi come back to us.'
                    />

                    <ul className='mt-10 grid gap-4 sm:grid-cols-2'>
                        {reasons.map(reason => (
                            <li
                                key={reason.title}
                                className='rounded-2xl border border-border bg-white p-5 shadow-sm transition-shadow hover:shadow-md sm:p-6'
                            >
                                <span className='gold-gradient flex size-11 items-center justify-center rounded-xl text-plum-deep shadow-sm'>
                                    <reason.icon className='size-5' aria-hidden='true' />
                                </span>

                                <h3 className='mt-4 font-heading text-[0.95rem] font-semibold leading-snug text-plum'>
                                    {reason.title}
                                </h3>

                                <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                    {reason.text}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    )
}
